<template>
  <div class="login-container">
    <div class="login-header">
      <img class="login-logo" />
    </div>

    <div class="verification-content">
      <h2 class="title">{{ title }}</h2>
      <p class="description" v-html="description"></p>
      <img :src="imageSrc" alt="Mail" class="email-image" />
      <p class="timer">{{ formattedTime }}</p>
    </div>

    <Footer />

    <div v-if="isLoading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script setup>

import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import Footer from "../components/Footer.vue";
import EmailConfirmationService from "../api/EmailConfirmationService.js";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import UserAuthService from "../api/UserAuthService.js";

const email = ref(localStorage.getItem("emailForSignIn") || "ваш email");
const isLoading = ref(false);
const formattedTime = ref("10:00");
const isVerified = ref(false);

import mailLock from "../assets/mail-lock.png";
import mailVerified from "../assets/mail-verified.png";

const imageSrc = ref(mailLock);

const title = ref("Письмо отправлено");
const description = ref(
  `Мы отправили письмо на <strong>${email.value}</strong>. Перейдите в почту и нажмите на ссылку.`
);

const router = useRouter();

const TOTAL_TIME_SECONDS = 600;
const TIMER_KEY = "emailVerificationStartTimestamp";
let startTimestamp = localStorage.getItem(TIMER_KEY);

if (!startTimestamp) {
  startTimestamp = Date.now();
  localStorage.setItem(TIMER_KEY, startTimestamp);
} else {
  startTimestamp = parseInt(startTimestamp);
}

const timerInterval = ref(null);

const formatTime = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const updateTimer = () => {
  const now = Date.now();
  const elapsed = Math.floor((now - startTimestamp) / 1000);
  const remaining = TOTAL_TIME_SECONDS - elapsed;

  if (remaining > 0 && !isVerified.value) {
    formattedTime.value = formatTime(remaining);
  } else {
    formattedTime.value = "00:00";
    clearInterval(timerInterval.value);
  }
};

onMounted(() => {
  updateTimer();
  timerInterval.value = setInterval(updateTimer, 1000);

  let navigated = false; // защита от двойных пушей

  EmailConfirmationService.subscribe(email.value, async () => {
    if (navigated) return;
    console.log("✅ Подтверждение получено через Firestore!");

    // визуальный апдейт
    isVerified.value = true;
    imageSrc.value = mailVerified;

    if (timerInterval.value) {
      clearInterval(timerInterval.value);
      timerInterval.value = null;
    }
    localStorage.removeItem(TIMER_KEY);
    localStorage.setItem("reg_email_verified", "true");

    navigated = true;
    EmailConfirmationService.unsubscribe();

    try {
    const auth = getAuth();
    const storedEmail = localStorage.getItem("reg_email") || localStorage.getItem("emailForSignIn");
    const storedPassword = localStorage.getItem("user_password");

    if (!storedEmail || !storedPassword) {
      console.warn("⚠️ Нет email/пароля в localStorage для автологина");
    } else {
      console.log("🔑 Выполняем signInWithEmailAndPassword для", storedEmail);

      await signInWithEmailAndPassword(auth, storedEmail, storedPassword);

      const user = auth.currentUser;
      if (!user) throw new Error("❌ Firebase user не найден после логина");

      await UserAuthService.checkUserStatus(user.uid, 5);
      console.log("✅ Firebase Auth сессия восстановлена и статус проверен");
    }
  } catch (err) {
    console.error("❌ Ошибка при автологине:", err);
  }

    console.log("🧭 Переход на /registration (первичная регистрация)");
    router.push("/registration");
  });
});

onBeforeUnmount(() => {
  EmailConfirmationService.unsubscribe();
  if (timerInterval.value) {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
  }
});

</script>
  
  <style scoped>

  .login-container {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    background-color: var(--bg);
    overflow-x: hidden;
    position: relative;
  }
  
  .login-header {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 750px;
    background-size: cover;
    background-position: center;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 20px;
  }
  
  [data-theme="dark"] .login-header {
    background-image: url("../assets/login_header_dark.webp");
  }
  
  [data-theme="light"] .login-header {
    background-image: url("../assets/login_header_light.webp");
  }
  
  .login-logo {
    width: 180px;
    height: auto;
    max-width: 100%;
    transform: translateY(-20px);
    content: url("../assets/login_logo_dark.png");
  }
  
  [data-theme="light"] .login-logo {
    content: url("../assets/login_logo_light.png");
  }
  
  footer {
    width: 100%;
    z-index: 5;
  }
  
  .modal-overlay {

  position: fixed;
  inset: 0; 
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; 
}

.verification-content {
  margin-top: 180px;
  text-align: center;
  z-index: 2;
  position: relative;
  font-family: "Eastman-Regular", sans-serif;
  padding: 0 20px;
  letter-spacing: -0.4px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  font-family: "Eastman-Bold", sans-serif;
  letter-spacing: -0.9px;
}

.description {
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 24px;
  width: 500px;
}

.description strong {
  font-weight: bold;
}

.email-image {
  width: 120px;
  margin: 20px 0;
}

.timer {
  font-size: 18px;
  font-weight: bold;
  margin-top: 10px;
}

@media (max-width: 768px) {

/* уменьшаем header */

.login-header{
  height: 420px;
}

/* логотип меньше */

.login-logo{
  width: 140px;
}

/* основной блок */

.verification-content{
  margin-top: 140px;
  padding: 0 20px;
}

/* заголовок */

.title{
  font-size: 20px;
}

/* текст */

.description{
  font-size: 14px;
  width: 100%;
  max-width: 320px;
  margin: 0 auto 20px auto;
}

/* картинка */

.email-image{
  width: 90px;
  margin: 18px 0;
}

/* таймер */

.timer{
  font-size: 16px;
}

}

  </style>